import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react-lite";
import {
  checkValidNumberString,
  parseFloatToBigNumber,
  formatRounded,
  FIXED_POINT,
  ZERO,
  MIN_SHOW_STAKE,
} from "../../../../services/numbers";
import StakingWrapper from "../../../../services/staking.wrapper";
import { debugLog, formatThousand } from "../../../../utils/helpers";
import appStore from "../../../../store/app.store";
import AmountInput from "../AmountInput";

import { Button, networkById, Notify } from "@airdao/ui-library";
import { useAccount } from "wagmi";
import { useEthersAdapter } from "@airdao/ui-library";

const ambChainId = +process.env.REACT_APP_CHAIN_ID;

const explorerUrl = networkById[ambChainId].blockExplorers.default.url;

const Withdraw = observer(({ withdrawContractInfo }) => {
  const { myStakeInAMB } = withdrawContractInfo;
  const [inputValue, setInputValue] = useState("");
  const [isFullWithdrawal, setFullWithdrawal] = useState(false);
  const [afterWithdraw, setAfterWithdraw] = useState(
    () => myStakeInAMB || ZERO
  );

  const { isConnected: isActive, address: account, chainId } = useAccount();
  const { provider } = useEthersAdapter();

  const [balance, setBalance] = useState(ZERO);

  const isReady = isActive && chainId === ambChainId;

  const refreshProc = async () => {
    if (provider && isActive) {
      provider.getBalance(account).then((balanceObj) => {
        setBalance(balanceObj);
      });
    }
  };

  const withdrawPayment = async () => {
    if (!checkValidNumberString(inputValue)) {
      return false;
    }

    const tx = await StakingWrapper.unstake(
      withdrawContractInfo,
      inputValue,
      isFullWithdrawal
    );

    debugLog("withdraw", tx);

    if (!tx) {
      Notify.error("Failed to create transaction.", null, { autoClose: 5000 });
    }

    setInputValue("");

    Notify.loading(`Transaction pending.`, null, { autoClose: 5000 });

    try {
      await tx.wait();

      Notify.success(
        `Transaction successful!`,
        <a
          href={`${explorerUrl}tx/${tx.hash}`}
          style={{ textDecoration: "underline" }}
          target={"_blank"}
        >
          View on explorer
        </a>,
        { autoClose: 5000 }
      );
    } catch (err) {
      console.log(err);
      Notify.error(
        "Transaction failed.",
        <a
          href={`${explorerUrl}tx/${tx.hash}`}
          style={{ textDecoration: "underline" }}
          target={"_blank"}
        >
          View on explorer
        </a>,
        { autoClose: 5000 }
      );
      return false;
    }
    window.dataLayer.push({
      event: "Withdraw",
    });
    return appStore.setRefresh();
  };

  const calculateSumAfterWithdraw = useCallback(
    () =>
      myStakeInAMB &&
      checkValidNumberString(inputValue) &&
      setAfterWithdraw(myStakeInAMB.sub(parseFloatToBigNumber(inputValue))),
    [inputValue]
  );

  useEffect(() => {
    calculateSumAfterWithdraw();
    refreshProc();
    return () => {
      calculateSumAfterWithdraw();
    };
  }, [inputValue, myStakeInAMB]);

  useEffect(() => {
    if (!isActive) {
      setInputValue("");
    }
  }, [isActive]);

  return (
    <div className="deposit">
      <AmountInput
        inputValue={inputValue}
        setInputValue={setInputValue}
        balance={myStakeInAMB}
        placeholder="0"
        label="Amount"
        disabled={!isReady}
        fullWithdrawal={setFullWithdrawal}
      />

      <div className={"deposit__balances"}>
        <div className={"deposit__balances-item"}>
          <div className={"deposit__balances-name"}>Available to withdraw</div>
          <div className={"deposit__balances-value"}>
            {myStakeInAMB && myStakeInAMB.gte(MIN_SHOW_STAKE)
              ? formatThousand(formatRounded(myStakeInAMB, 2))
              : 0}{" "}
            AMB
          </div>
        </div>
      </div>

      <Button
        type={"primary"}
        size={"large"}
        disabled={
          !checkValidNumberString(inputValue) ||
          parseFloatToBigNumber(inputValue).eq(0) ||
          parseFloatToBigNumber(inputValue).gt(
            myStakeInAMB.add(FIXED_POINT.div(2))
          ) ||
          !isReady
        }
        onClick={withdrawPayment}
        className={"deposit__button"}
      >
        {!isReady ? "Connect Wallet" : "Withdraw"}
      </Button>
    </div>
  );
});

Withdraw.propTypes = {
  withdrawContractInfo: PropTypes.any,
};

export default Withdraw;
